import React, { useEffect, useState } from 'react'
import { authService } from '../../services/auth'
import { useI18n } from '../../i18n/I18nContext'
import { MfaSettings } from './MfaSettings'

type SafeSession = {
  sessionId: string
  createdAt: string
  lastActiveAt: string
  deviceLabel: string
  authenticationMethod: string
  mfaAssurance: string
  expiresAt: string
  currentSession: boolean
}

export const SecuritySessions: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { t, formatDateTime } = useI18n()
  const [sessions, setSessions] = useState<SafeSession[]>([])
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)

  const refresh = async () => {
    const response = await authService.listSessions()
    setSessions(response.data?.sessions || [])
  }

  useEffect(() => {
    refresh().catch(() => setMessage(t('Active sessions could not be loaded.')))
  }, [t])

  const revoke = async (sessionId: string) => {
    setBusy(true)
    setMessage('')
    try {
      await authService.revokeSession(sessionId)
      await refresh()
      setMessage(t('Session revoked.'))
    } catch (error: any) {
      setMessage(error.response?.data?.message || t('Session could not be revoked.'))
    } finally {
      setBusy(false)
    }
  }

  const revokeOthers = async () => {
    setBusy(true)
    try {
      const response = await authService.revokeOtherSessions()
      await refresh()
      setMessage(t('{count} other session(s) revoked.', { count: response.data?.revokedCount || 0 }))
    } catch (error: any) {
      setMessage(error.response?.data?.message || t('Other sessions could not be revoked.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <section className="modal-card stack" role="dialog" aria-modal="true" aria-labelledby="active-sessions-heading">
        <div className="inline-group" style={{ justifyContent: 'space-between' }}>
          <h2 id="active-sessions-heading">{t('Active sessions')}</h2>
          <button type="button" className="btn btn-ghost" onClick={onClose}>{t('Close')}</button>
        </div>
        <p className="helper">{t('Review devices signed in to your account. Network addresses and token details are never displayed.')}</p>
        {message ? <p role="status">{message}</p> : null}
        <MfaSettings />
        <div className="stack">
          {sessions.map((session) => (
            <article className="card" key={session.sessionId}>
              <div className="inline-group" style={{ justifyContent: 'space-between' }}>
                <div>
                  <strong>{session.deviceLabel || t('Unknown device')}{session.currentSession ? ` — ${t('Current session')}` : ''}</strong>
                  <p className="helper">
                    {t('Last active')} {formatDateTime(session.lastActiveAt)} · {session.authenticationMethod} · {session.mfaAssurance}
                  </p>
                  <p className="helper">{t('Expires')} {formatDateTime(session.expiresAt)}</p>
                </div>
                <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => revoke(session.sessionId)}>
                  {session.currentSession ? t('Sign out this session') : t('Revoke')}
                </button>
              </div>
            </article>
          ))}
          {!sessions.length ? <p>{t('No active sessions.')}</p> : null}
        </div>
        <button type="button" className="btn btn-secondary" disabled={busy} onClick={revokeOthers}>{t('Sign out other sessions')}</button>
      </section>
    </div>
  )
}
